import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { ApiDesiredLocationPrimary } from '@/types/api/candidate';

export interface DesiredLocation {
  id: number;
  name: string;
  parentId: number;
  parentName: string;
  cities: Array<{
    id: number;
    name: string;
  }>;
}

export interface DesiredOccupation {
  occupation_id: number;
  name: string;
  index: number;
}

export interface DesiredIndustry {
  industry_id: number;
  name: string;
}

interface DesiredConditionPayload {
  candidate_resume_desired_locations?: ApiDesiredLocationPrimary[];
  desired_annual_income?: number | null;
  candidate_resume_desired_industries?: DesiredIndustry[];
  candidate_resume_desired_occupations?: DesiredOccupation[];
  willingness?: string;
}

export const useDesiredConditionStore = defineStore('desiredCondition', () => {
  // 希望勤務地
  const desired_locations = ref<DesiredLocation[]>([]);

  // 希望年収
  const desired_annual_income = ref<number | null>(null);

  // 希望業種
  const desired_industries = ref<DesiredIndustry[]>([]);

  // 希望職種
  const desired_occupations = ref<DesiredOccupation[]>([]);

  // 転職意向度
  const willingness = ref<string>('');

  // 希望勤務地の設定
  const setDesiredLocations = (locations: DesiredLocation[]): void => {
    desired_locations.value = locations;
  };

  // 希望勤務地の削除
  const removeDesiredLocation = (prefectureId: number): void => {
    desired_locations.value = desired_locations.value.filter(
      location => location.id !== prefectureId
    );
  };

  // 希望職種の設定
  const setDesiredOccupations = (occupations: DesiredOccupation[]): void => {
    desired_occupations.value = occupations.map((occupation, index) => ({
      ...occupation,
      index: occupation.index || index,
    }));
  };

  // 希望職種の削除
  const removeDesiredOccupation = (occupationId: number): void => {
    desired_occupations.value = desired_occupations.value.filter(
      occupation => occupation.occupation_id !== occupationId
    );
  };

  // 希望業種の設定
  const setDesiredIndustries = (industries: DesiredIndustry[]): void => {
    desired_industries.value = industries;
  };

  // 希望業種の削除
  const removeDesiredIndustry = (industryId: number): void => {
    desired_industries.value = desired_industries.value.filter(
      industry => industry.industry_id !== industryId
    );
  };

  // データの設定
  const setDesiredConditionData = (data: DesiredConditionPayload): void => {
    if (data.candidate_resume_desired_locations) {
      const groupedByPrefecture = data.candidate_resume_desired_locations.reduce(
        (acc: Record<number, DesiredLocation>, location) => {
          if (!acc[location.prefecture_id]) {
            acc[location.prefecture_id] = {
              id: location.prefecture_id,
              name: location.prefecture_name,
              parentId: location.parent_id || location.prefecture_id,
              parentName: location.parent_name || location.prefecture_name,
              cities: [],
            };
          }
          if (location.city_id && location.city_name) {
            acc[location.prefecture_id].cities.push({
              id: location.city_id,
              name: location.city_name,
            });
          }
          return acc;
        },
        {}
      );

      desired_locations.value = Object.values(groupedByPrefecture);
    }

    if (data.desired_annual_income) {
      desired_annual_income.value = data.desired_annual_income;
    }

    if (data.candidate_resume_desired_industries) {
      desired_industries.value = data.candidate_resume_desired_industries.map(industry => ({
        industry_id: industry.industry_id,
        name: industry.name,
      }));
    }

    if (data.candidate_resume_desired_occupations) {
      desired_occupations.value = data.candidate_resume_desired_occupations.map(
        (occupation, index) => ({
          occupation_id: occupation.occupation_id,
          name: occupation.name,
          index,
        })
      );
    }

    if (data.willingness) {
      willingness.value = data.willingness;
    }
  };

  // ストアのリセット
  const resetStore = (): void => {
    desired_locations.value = [];
    desired_annual_income.value = null;
    desired_industries.value = [];
    desired_occupations.value = [];
    willingness.value = '';
  };

  return {
    desired_locations,
    desired_annual_income,
    desired_industries,
    desired_occupations,
    willingness,
    setDesiredLocations,
    removeDesiredLocation,
    setDesiredOccupations,
    removeDesiredOccupation,
    setDesiredIndustries,
    removeDesiredIndustry,
    setDesiredConditionData,
    resetStore,
  };
});
